// pasteOnlySystemFonts.ts
import { Extension } from '@tiptap/core';

export const SYSTEM_FONTS = [
  'Montserrat, Helvetica, Arial, serif',
  'Arial',
  'Comic Sans MS',
  'Courier New',
  'Georgia',
  'Lucida Sans Unicode',
  'Tahoma',
  'Times New Roman',
  'Trebuchet MS',
  'Verdana',
  'sans-serif'
];

function normalizeFontName(s: string) {
  return String(s || '')
    .toLowerCase()
    .replace(/["']/g, '')
    .trim();
}

function buildAllowedFontMap(families: string[]) {
  const map = new Map<string, string>();
  families.forEach(fam => {
    fam.split(',').forEach(font => {
      const key = normalizeFontName(font);
      if (key && !map.has(key)) {
        map.set(key, fam);
      }
    });
  });
  return map;
}

function sanitizeHtmlFonts(htmlString: string, allowedMap: Map<string, string>) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  // remove style blocks and external links
  doc.querySelectorAll('style, link[rel="stylesheet"]').forEach(n => n.remove());

  // convert <font face="..."> -> span with sanitized font
  doc.querySelectorAll('font[face]').forEach(f => {
    const span = doc.createElement('span');
    while (f.firstChild) span.appendChild(f.firstChild);
    const face = f.getAttribute('face') || '';
    const fonts = face.split(',').map(s => normalizeFontName(s));
    let chosen: string | null = null;
    for (const ft of fonts) {
      if (allowedMap.has(ft)) { chosen = allowedMap.get(ft)!; break; }
    }
    if (chosen) span.style.fontFamily = chosen;
    if (f.getAttribute('color')) span.style.color = f.getAttribute('color')!;
    f.parentNode!.replaceChild(span, f);
  });

  const all = doc.querySelectorAll('*');
  all.forEach((el: HTMLElement) => {
    const rawStyle = el.getAttribute('style') || '';
    if (/@import|@font-face|fonts\.googleapis|url\(/i.test(rawStyle)) {
      el.removeAttribute('style');
      return;
    }

    if (/font-family\s*:/i.test(rawStyle)) {
      const match = rawStyle.match(/font-family\s*:\s*([^;]+)/i);
      if (match) {
        const familyStr = match[1];
        const fonts = familyStr.split(',').map(s => s.trim().replace(/^["']|["']$/g, ''));
        let chosen: string | null = null;
        for (const fName of fonts) {
          const n = normalizeFontName(fName);
          if (allowedMap.has(n)) {
            chosen = allowedMap.get(n)!;
            break;
          }
        }
        if (chosen) {
          el.style.fontFamily = chosen;
        } else {
          el.style.removeProperty('font-family');
          if (!el.getAttribute('style') || el.getAttribute('style')!.trim() === '') {
            el.removeAttribute('style');
          }
        }
      }
    }
  });

  return doc.body.innerHTML;
}

export const pasteOnlySystemFonts = Extension.create({
  name: 'pasteOnlySystemFonts',

  addOptions() {
    return {
      allowedFamilies: SYSTEM_FONTS,
    };
  },

  // Type-safe storage where we keep the handler
  addStorage() {
    return {
      pasteHandler: null as ((event: ClipboardEvent) => void) | null,
    };
  },

  onCreate() {
    const editor = this.editor;
    const allowedMap = buildAllowedFontMap(this.options.allowedFamilies);

    // store handler in storage (so TS knows the field exists)
    this.storage.pasteHandler = (event: ClipboardEvent) => {
      const clipboard = event.clipboardData;
      if (!clipboard) return;
      const html = clipboard.getData('text/html');
      if (!html) return; // let default behavior for plain text
      event.preventDefault();

      const sanitized = sanitizeHtmlFonts(html, allowedMap);
      // Insert sanitized HTML
      editor.commands.insertContent(sanitized.trim());
    };

    editor.view.dom.addEventListener('paste', this.storage.pasteHandler!, true);
  },

  onDestroy() {
    const handler = this.storage.pasteHandler;
    if (handler && this.editor && this.editor.view && this.editor.view.dom) {
      this.editor.view.dom.removeEventListener('paste', handler, true);
    }
  },
});
